import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Users, Briefcase, CalendarDays, IndianRupee, Trophy, Shield,
  BarChart3, ArrowRight, CheckCircle, Target, Award, Dumbbell,
  ClipboardCheck, Brain, FileText, Play, ChevronDown
} from 'lucide-react';
import { LOGO_URL, BrandText } from '@/lib/brand';

function useCounter(end, dur = 2000) {
  const [c, setC] = useState(0);
  const ref = useRef(null);
  const v = useInView(ref, { once: true });
  useEffect(() => { if (!v) return; let s = 0; const step = Math.max(1, Math.floor(end / (dur / 16))); const t = setInterval(() => { s += step; if (s >= end) { setC(end); clearInterval(t); } else setC(s); }, 16); return () => clearInterval(t); }, [v, end, dur]);
  return { count: c, ref };
}

function Anim({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const v = useInView(ref, { once: true, margin: '-40px' });
  return <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={v ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.45, delay, ease: 'easeOut' }} className={className}>{children}</motion.div>;
}

// 12 unique background colors for each module
const moduleBgs = [
  'bg-emerald-50/70 dark:bg-emerald-950/15',
  'bg-blue-50/70 dark:bg-blue-950/15',
  'bg-cyan-50/70 dark:bg-cyan-950/15',
  'bg-indigo-50/70 dark:bg-indigo-950/15',
  'bg-orange-50/70 dark:bg-orange-950/15',
  'bg-green-50/70 dark:bg-green-950/15',
  'bg-teal-50/70 dark:bg-teal-950/15',
  'bg-violet-50/70 dark:bg-violet-950/15',
  'bg-amber-50/70 dark:bg-amber-950/15',
  'bg-rose-50/70 dark:bg-rose-950/15',
  'bg-purple-50/70 dark:bg-purple-950/15',
  'bg-slate-50/70 dark:bg-slate-950/15',
];

const modules = [
  { icon: BarChart3, title: 'Academy Dashboard', desc: 'Real-time KPI cards, revenue vs expenses charts, quick action buttons, and recent activity feed.', features: ['KPI cards with live metrics', 'Revenue vs Expenses chart', 'Quick action shortcuts', 'Recent activity timeline'], gradient: 'from-emerald-500 to-teal-600', screenshot: '/screenshots/dashboard.jpg' },
  { icon: Users, title: 'Student Management', desc: 'Complete student lifecycle — enrollment, profiles, BMI tracking, batch assignments, and guardian contacts.', features: ['Student profiles with IDs', 'Auto BMI calculation', 'Sport & batch assignment', 'Search, filter & CSV export'], gradient: 'from-blue-500 to-indigo-600', screenshot: '/screenshots/students.jpg' },
  { icon: ClipboardCheck, title: 'Attendance Tracking', desc: 'One-click P/A/L marking for every student. Date picker, batch filters, and bulk "All Present" action.', features: ['Quick P/A/L buttons', 'Date & batch filtering', 'Bulk "All Present" action', 'Attendance rate analytics'], gradient: 'from-cyan-500 to-sky-600', screenshot: '/screenshots/attendance.jpg' },
  { icon: Briefcase, title: 'Smart HRMS', desc: 'Employee management with KPI overview — headcount, coach ratio, monthly payroll, and average salary.', features: ['Employee KPI dashboard', 'Role & department filters', 'Salary & payroll tracking', 'Leaves & attendance tabs'], gradient: 'from-indigo-500 to-violet-600', screenshot: '/screenshots/hrms.jpg' },
  { icon: CalendarDays, title: 'Event Management', desc: 'Create tournaments, camps, and trials. AI poster generation, QR registration, and participant tracking.', features: ['Tournament & camp creation', 'QR registration links', 'AI poster generation', 'Participant management'], gradient: 'from-orange-500 to-amber-600', screenshot: '/screenshots/events.jpg' },
  { icon: IndianRupee, title: 'Finance & P&L', desc: 'Revenue tracking, expense management, and profitability analysis with category breakdowns and AI insights.', features: ['Revenue vs Expenses trend', 'Category pie charts', 'P&L KPI dashboard', 'AI financial insights'], gradient: 'from-green-500 to-emerald-600', screenshot: '/screenshots/finance.jpg' },
  { icon: FileText, title: 'GST Invoicing', desc: 'GST-compliant invoices with 18% tax, QR codes, and AI duplicate detection at 99.2% accuracy.', features: ['GST 18% auto-calculation', 'AI duplicate detection', 'WhatsApp reminders', 'PDF download & print'], gradient: 'from-teal-500 to-green-600', screenshot: '/screenshots/finance.jpg' },
  { icon: FileText, title: 'Payroll System', desc: 'Run monthly payroll with bonus/deductions. Approve to auto-create salary expenses with full history.', features: ['One-click payroll run', 'Bonus & deductions', 'Auto expense creation', 'Payslip history'], gradient: 'from-violet-500 to-purple-600', screenshot: '/screenshots/hrms.jpg' },
  { icon: Award, title: 'Progress Cards', desc: '5-star skill assessments by coaches with written feedback, video uploads, and PDF reports.', features: ['Star skill ratings', 'Coach written feedback', 'Video analysis uploads', 'PDF progress reports'], gradient: 'from-amber-500 to-yellow-600', screenshot: '/screenshots/students.jpg' },
  { icon: Target, title: 'CRM & Leads', desc: 'Track inquiries, trial sessions, and conversion pipeline to grow your student base systematically.', features: ['Lead tracking pipeline', 'Trial session management', 'Follow-up automation', 'Conversion analytics'], gradient: 'from-rose-500 to-pink-600', screenshot: '/screenshots/dashboard.jpg' },
  { icon: Brain, title: 'AI Analytics', desc: 'Revenue forecasts, collection probability scoring, and smart action recommendations with confidence levels.', features: ['Revenue forecasting', 'Collection probability', 'Smart recommendations', '99.2% AI accuracy'], gradient: 'from-purple-500 to-fuchsia-600', screenshot: '/screenshots/dashboard.jpg' },
  { icon: Shield, title: 'Enterprise Security', desc: 'Multi-tenant isolation, five user roles, JWT authentication, and bcrypt password hashing.', features: ['5-role RBAC system', 'Multi-tenant isolation', 'JWT authentication', 'Encrypted passwords'], gradient: 'from-slate-500 to-zinc-600', screenshot: '/screenshots/hrms.jpg' },
];

export default function Features() {
  const [sticky, setSticky] = useState(false);
  const s1 = useCounter(500, 2000), s2 = useCounter(25000, 2500), s3 = useCounter(143, 1500);
  useEffect(() => { const h = () => setSticky(window.scrollY > 400); window.addEventListener('scroll', h, { passive: true }); return () => window.removeEventListener('scroll', h); }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="features-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-card/90 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
          <Link to="/" className="flex items-center gap-2.5"><img src={LOGO_URL} alt="Out-Play" className="h-9 w-9 rounded-lg object-cover" /><BrandText className="text-xl tracking-tight" /></Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Home</Link>
            <span className="text-sm text-foreground font-medium">Features</span>
            <Link to="/faq" className="text-sm text-muted-foreground hover:text-foreground transition-colors">FAQ</Link>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login"><Button variant="ghost" size="sm">Log in</Button></Link>
            <Link to="/signup"><Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-600/20">Free Trial <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
          </div>
        </div>
      </nav>

      {/* Hero - compact */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-background to-blue-50/30 dark:from-emerald-950/20 dark:via-background dark:to-blue-950/10" />
        <div className="absolute top-16 right-16 w-64 h-64 bg-emerald-400/10 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 px-4 py-1.5 mb-5">
              <Trophy className="h-3.5 w-3.5 mr-1.5" /> 12 Modules • Real Champions FC Screenshots
            </Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold tracking-tight text-foreground leading-[1.1] max-w-3xl mx-auto">
              Every Feature Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500">Academy Needs</span>
            </h1>
            <p className="text-sm text-muted-foreground mt-3 max-w-xl mx-auto">Real screenshots from Champions Football Academy. 12 integrated modules built for Indian sports academies.</p>
            {/* Stats inline */}
            <div className="flex flex-wrap justify-center gap-6 mt-5">
              <div className="text-center"><p ref={s1.ref} className="text-xl font-display font-bold text-emerald-600 tabular-nums">{s1.count}+</p><p className="text-[10px] text-muted-foreground">Academies</p></div>
              <div className="text-center"><p ref={s2.ref} className="text-xl font-display font-bold text-blue-600 tabular-nums">{s2.count.toLocaleString()}+</p><p className="text-[10px] text-muted-foreground">Students</p></div>
              <div className="text-center"><p ref={s3.ref} className="text-xl font-display font-bold text-amber-600 tabular-nums">₹{s3.count}K</p><p className="text-[10px] text-muted-foreground">Payroll</p></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 12 Modules - each with unique bg color, no CTA buttons */}
      <section id="modules">
        {modules.map((mod, i) => {
          const Icon = mod.icon;
          const isEven = i % 2 === 0;
          const bg = moduleBgs[i];

          return (
            <Anim key={mod.title + i} delay={0.05}>
              <div className={`${bg} border-b border-border/30`} data-testid={`feature-module-${i}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
                  <div className={`grid md:grid-cols-2 gap-8 lg:gap-12 items-center ${isEven ? '' : 'md:grid-flow-dense'}`}>
                    {/* Text */}
                    <div className={`space-y-4 ${isEven ? '' : 'md:col-start-2'}`}>
                      <div className="flex items-center gap-3">
                        <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${mod.gradient} flex items-center justify-center shadow-md`}>
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <span className="text-[10px] font-bold text-muted-foreground tracking-widest uppercase">Module {String(i + 1).padStart(2, '0')}</span>
                          <h3 className="text-lg font-display font-bold text-foreground leading-tight">{mod.title}</h3>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">{mod.desc}</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                        {mod.features.map(f => (
                          <div key={f} className="flex items-center gap-1.5">
                            <CheckCircle className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                            <span className="text-xs text-foreground">{f}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Screenshot */}
                    <div className={`${isEven ? '' : 'md:col-start-1 md:row-start-1'}`}>
                      <div className="rounded-xl overflow-hidden border border-border/50 shadow-lg bg-card">
                        <img
                          src={mod.screenshot}
                          alt={`${mod.title} — Champions Football Academy`}
                          className="w-full object-cover object-left-top"
                          loading="lazy"
                          style={{ maxHeight: '280px' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Anim>
          );
        })}
      </section>

      {/* Bottom CTA */}
      <section className="relative py-16 lg:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-700" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.08),transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <Anim>
            <Trophy className="h-10 w-10 mx-auto mb-4 text-emerald-200" />
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">See It Live with Your Own Data</h2>
            <p className="text-emerald-100 mb-6 max-w-md mx-auto text-sm">Start free and explore every module with your academy's real data. 14-day trial, no credit card.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/signup"><Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50 shadow-xl h-12 px-8 text-base rounded-xl">Start Free Trial <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
              <Link to="/login"><Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 h-12 px-8 text-base rounded-xl">Login to Demo</Button></Link>
            </div>
          </Anim>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-card py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center gap-3">
          <div className="flex items-center gap-2"><img src={LOGO_URL} alt="Out-Play" className="h-6 w-6 rounded object-cover" /><BrandText className="text-sm" /><span className="text-xs text-muted-foreground">&copy; 2026</span></div>
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <Link to="/faq" className="hover:text-foreground transition-colors">FAQ</Link>
            <Link to="/signup" className="hover:text-foreground transition-colors">Free Trial</Link>
          </div>
        </div>
      </footer>

      {/* Sticky CTA */}
      <motion.div initial={{ y: 100 }} animate={{ y: sticky ? 0 : 100 }} transition={{ type: 'spring', stiffness: 300, damping: 30 }} className="fixed bottom-0 left-0 right-0 z-40 bg-emerald-600 text-white shadow-2xl py-2.5 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="hidden sm:block"><p className="text-xs font-semibold">See all 12 modules live</p><p className="text-[11px] text-emerald-100">Champions FC data • 14-day free trial</p></div>
          <p className="sm:hidden text-xs font-semibold">Start Free Trial</p>
          <Link to="/signup"><Button size="sm" className="bg-white text-emerald-700 hover:bg-emerald-50 shadow-lg">Get Started <ArrowRight className="ml-1 h-3.5 w-3.5" /></Button></Link>
        </div>
      </motion.div>
    </div>
  );
}
